import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { useEnvStore } from '@/shared/store/envStore'
import { useAuthStore } from '@/shared/store/authStore'
import { listenFromHost, postToHost } from '@/shared/bridge/bridge'
import { usesHostSession } from '@/shared/bridge/env'
import type { ApiError, ServerError, ServerSuccess, UserRequestConfig } from '@/shared/types/http'
import { queryClient } from './queryClient'
import { navigateToLogin } from '@/shared/lib/navigateToLogin'

type RetriableRequestConfig = InternalAxiosRequestConfig & UserRequestConfig & {
  _retry?: boolean
}

export function isApiError(e: unknown): e is ApiError {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { name?: string }).name === 'ApiError' &&
    'status' in e &&
    (typeof (e as ApiError).status === 'number' || (e as ApiError).status === null)
  )
}

const instance = axios.create({
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

let refreshPromise: Promise<string | null> | null = null

function isServerError(data: unknown): data is ServerError {
  return (
    !!data &&
    typeof data === 'object' &&
    'statusCode' in data &&
    (data as { statusCode: number }).statusCode !== 200
  )
}

function withDefaultAuth(config?: UserRequestConfig): UserRequestConfig {
  return { needAuth: config?.needAuth ?? true, ...config }
}

function requestConfig(body: unknown, config?: UserRequestConfig): UserRequestConfig {
  const next = withDefaultAuth(config)
  if (body instanceof FormData) {
    const headers = { ...(next.headers as Record<string, string> | undefined) }
    delete headers['Content-Type']
    delete headers['content-type']
    return { ...next, headers }
  }
  return next
}

function toApiError(error: AxiosError): ApiError {
  const data = error.response?.data as
    | { code?: string; message?: string; error?: string; errorMessage?: string; trace?: string }
    | undefined
  return {
    name: 'ApiError',
    status: error.response?.status ?? null,
    code: data?.code ?? error.code,
    message: data?.message ?? data?.errorMessage ?? data?.error ?? error.message ?? '알 수 없는 오류가 발생했습니다.',
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    details: error.response?.data,
    raw: error,
  }
}

function isServerSuccess<T>(data: unknown): data is ServerSuccess<T> {
  return (
    !!data &&
    typeof data === 'object' &&
    'data' in data &&
    typeof (data as { statusCode?: unknown }).statusCode === 'number'
  )
}

async function requestTokenRefresh(refreshToken: string) {
  const baseURL = useEnvStore.getState().baseUrl
  const response = await axios.post<ServerSuccess<{ accessToken: string; refreshToken?: string }> | {
    accessToken: string
    refreshToken?: string
  }>(
    '/api/auth/refresh',
    { refreshToken },
    {
      baseURL,
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return isServerSuccess<{ accessToken: string; refreshToken?: string }>(response.data)
    ? response.data.data
    : response.data
}

const HOST_REFRESH_TIMEOUT_MS = 10_000

// 앱/OMS 가 토큰을 쥐고 있다. 갱신을 요청하고 SET_TOKEN 을 기다린다.
// 구버전 호스트는 REQUEST_TOKEN_REFRESH 를 모르므로 타임아웃 → null → 기존 SESSION_EXPIRED 흐름으로 떨어진다.
function requestHostTokenRefresh() {
  return new Promise<string | null>((resolve) => {
    const finish = (token: string | null) => {
      window.clearTimeout(timer)
      unlisten()
      resolve(token)
    }
    const timer = window.setTimeout(() => finish(null), HOST_REFRESH_TIMEOUT_MS)
    const unlisten = listenFromHost((msg) => {
      if (msg.type === 'SET_TOKEN') finish(msg.payload.accessToken || null)
      else if (msg.type === 'TOKEN_REFRESH_FAILED') finish(null)
    })
    postToHost({ type: 'REQUEST_TOKEN_REFRESH' })
  }).then((token) => {
    if (token) useAuthStore.getState().setToken(token, null)
    return token
  })
}

async function refreshAccessToken() {
  if (usesHostSession()) {
    refreshPromise ??= requestHostTokenRefresh().finally(() => {
      refreshPromise = null
    })
    return refreshPromise
  }

  const refreshToken = useAuthStore.getState().refreshToken
  if (!refreshToken) return null

  refreshPromise ??= requestTokenRefresh(refreshToken)
    .then((tokens) => {
      if (!tokens.accessToken) return null
      useAuthStore.getState().setToken(tokens.accessToken, tokens.refreshToken ?? refreshToken)
      return tokens.accessToken
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

function handleUnauthorized(apiError: ApiError, error: AxiosError) {
  const isLoginRequest = error.config?.url?.includes('/api/auth/login')
  const isRefreshRequest = error.config?.url?.includes('/api/auth/refresh')
  if (isLoginRequest || isRefreshRequest) return

  useAuthStore.getState().logout()
  queryClient.clear()

  if (usesHostSession()) {
    postToHost({ type: 'SESSION_EXPIRED' })
  } else {
    void navigateToLogin('expired')
  }
  return apiError
}

instance.interceptors.request.use((config: InternalAxiosRequestConfig & UserRequestConfig) => {
  const baseURL = useEnvStore.getState().baseUrl
  if (baseURL) config.baseURL = baseURL

  const token = useAuthStore.getState().accessToken
  if (config.needAuth !== false && token) config.headers.Authorization = `Bearer ${token}`

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  return config
})

instance.interceptors.response.use(
  (response) => {
    const config = response.config as InternalAxiosRequestConfig & UserRequestConfig
    if (isServerError(response.data)) {
      const error = new AxiosError(
        response.data.errorMessage || '서버 내부 오류가 발생했습니다.',
        String(response.data.statusCode),
        config,
        response.request,
        {
          ...response,
          status: response.data.statusCode,
          data: response.data,
        },
      )
      // HTTP 200 + 본문 statusCode 401 도 HTTP 401 과 같이 갱신/만료 처리한다.
      return handleResponseError(error)
    }

    if (config.rawResponse) return response
    if (
      response.data &&
      typeof response.data === 'object' &&
      'data' in (response.data as Record<string, unknown>)
    ) {
      response.data = (response.data as ServerSuccess<unknown>).data
    }
    return response
  },
  handleResponseError,
)

async function handleResponseError(error: AxiosError) {
  const apiError = toApiError(error)
  const originalConfig = error.config as RetriableRequestConfig | undefined

  if (
    apiError.status === 401 &&
    originalConfig &&
    originalConfig.needAuth !== false &&
    !originalConfig._retry &&
    !originalConfig.url?.includes('/api/auth/login') &&
    !originalConfig.url?.includes('/api/auth/refresh')
  ) {
    const accessToken = await refreshAccessToken()
    if (accessToken) {
      originalConfig._retry = true
      originalConfig.headers.Authorization = `Bearer ${accessToken}`
      return instance(originalConfig)
    }
  }

  if (apiError.status === 401 && originalConfig?.needAuth !== false) handleUnauthorized(apiError, error)

  return Promise.reject(apiError)
}

export function get<T>(url: string, config: UserRequestConfig & { rawResponse: true }): Promise<AxiosResponse<ServerSuccess<T>>>
export function get<T>(url: string, config?: UserRequestConfig): Promise<T>
export async function get<T>(url: string, config?: UserRequestConfig) {
  const response = await instance.get<T | ServerSuccess<T>>(url, withDefaultAuth(config))
  return config?.rawResponse ? response : (response.data as T)
}

export function post<T, B = unknown>(
  url: string,
  data: B,
  config: UserRequestConfig & { rawResponse: true },
): Promise<AxiosResponse<ServerSuccess<T>>>
export function post<T, B = unknown>(url: string, data?: B, config?: UserRequestConfig): Promise<T>
export async function post<T, B = unknown>(url: string, data?: B, config?: UserRequestConfig) {
  const response = await instance.post<T | ServerSuccess<T>>(url, data, requestConfig(data, config))
  return config?.rawResponse ? response : (response.data as T)
}

export function put<T, B = unknown>(
  url: string,
  data: B,
  config: UserRequestConfig & { rawResponse: true },
): Promise<AxiosResponse<ServerSuccess<T>>>
export function put<T, B = unknown>(url: string, data?: B, config?: UserRequestConfig): Promise<T>
export async function put<T, B = unknown>(url: string, data?: B, config?: UserRequestConfig) {
  const response = await instance.put<T | ServerSuccess<T>>(url, data, requestConfig(data, config))
  return config?.rawResponse ? response : (response.data as T)
}

export function patch<T, B = unknown>(
  url: string,
  data: B,
  config: UserRequestConfig & { rawResponse: true },
): Promise<AxiosResponse<ServerSuccess<T>>>
export function patch<T, B = unknown>(url: string, data?: B, config?: UserRequestConfig): Promise<T>
export async function patch<T, B = unknown>(url: string, data?: B, config?: UserRequestConfig) {
  const response = await instance.patch<T | ServerSuccess<T>>(url, data, requestConfig(data, config))
  return config?.rawResponse ? response : (response.data as T)
}

export function del<T>(url: string, config: UserRequestConfig & { rawResponse: true }): Promise<AxiosResponse<ServerSuccess<T>>>
export function del<T>(url: string, config?: UserRequestConfig): Promise<T>
export async function del<T>(url: string, config?: UserRequestConfig) {
  const response = await instance.delete<T | ServerSuccess<T>>(url, withDefaultAuth(config))
  return config?.rawResponse ? response : (response.data as T)
}

export const http = {
  get,
  post,
  put,
  patch,
  del,
}
